import React from "react"
import PropTypes from "prop-types"
import { Divider, Flex, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { numberToCurrency } from "helpers/string"
import { useTranslation } from "react-i18next"
import { Title, Text } from "ui/Typography"
import styles from "./index.module.css"
import { spacings } from "themes/variables"
import { formatTimeDuration } from "utils/experience"

/**
 * Enhanced Pricing Details Tooltip
 * Displays pricing information for single or multiple durations
 * Supports both flat rate and per person pricing types
 */
const PricingDetailsTooltip = ({
    tripPrices = []
}) => {
    const { t } = useTranslation()

    // If no pricing data, don't render
    if (!tripPrices || tripPrices.length === 0) {
        return null
    }

    // Helper function to render pricing display based on pricing type
    const renderPricingDisplay = (priceOption, durationLabel) => {
        const isFlatRate = priceOption.pricing_type === "charge_flat_rate"
        const isPerPerson = priceOption.pricing_type === "charge_per_person"

        if (isFlatRate) {
            return (
                <>
                    <Text size="lg" strong>
                        {durationLabel} - {numberToCurrency(priceOption.flat_rate)} Flat Rate
                    </Text>
                    <Text size="sm" className="color-text-secondary">
                        Includes {priceOption.included_guests || 0} guests
                    </Text>
                    {(Number(priceOption.price_per_person) > 0 ||
                        Number(priceOption.price_per_child) > 0 ||
                        Number(priceOption.price_per_infant) > 0 ||
                        Number(priceOption.price_per_passenger) > 0) && (
                            <Flex vertical gap={spacings.XXXS}>
                                <Text size="sm" strong>Additional:</Text>
                                {Number(priceOption.price_per_person) > 0 && (
                                    <Text size="sm" className="color-text-secondary">
                                        {numberToCurrency(priceOption.price_per_person)}/adult
                                    </Text>
                                )}
                                {Number(priceOption.price_per_child) > 0 && (
                                    <Text size="sm" className="color-text-secondary">
                                        {numberToCurrency(priceOption.price_per_child)}/child
                                    </Text>
                                )}
                                {Number(priceOption.price_per_infant) > 0 && (
                                    <Text size="sm" className="color-text-secondary">
                                        {numberToCurrency(priceOption.price_per_infant)}/infant
                                    </Text>
                                )}
                                {Number(priceOption.price_per_passenger) > 0 && (
                                    <Text size="sm" className="color-text-secondary">
                                        {numberToCurrency(priceOption.price_per_passenger)}/passenger
                                    </Text>
                                )}
                            </Flex>
                        )}
                </>
            )
        }

        if (isPerPerson) {
            return (
                <>
                    <Text size="lg" strong>
                        {durationLabel} - Per Person Pricing
                    </Text>
                    <Flex vertical gap={spacings.XXXS}>
                        {Number(priceOption.price_per_person) > 0 && (
                            <Text size="sm" className="color-text-secondary">
                                {numberToCurrency(priceOption.price_per_person)}/adult
                            </Text>
                        )}
                        {Number(priceOption.price_per_child) > 0 && (
                            <Text size="sm" className="color-text-secondary">
                                {numberToCurrency(priceOption.price_per_child)}/child
                            </Text>
                        )}
                        {Number(priceOption.price_per_infant) > 0 && (
                            <Text size="sm" className="color-text-secondary">
                                {numberToCurrency(priceOption.price_per_infant)}/infant
                            </Text>
                        )}
                        {Number(priceOption.price_per_passenger) > 0 && (
                            <Text size="sm" className="color-text-secondary">
                                {numberToCurrency(priceOption.price_per_passenger)}/passenger
                            </Text>
                        )}
                    </Flex>
                </>
            )
        }

        // Fallback for other pricing types
        return (
            <Text size="lg" strong>
                {durationLabel} - {numberToCurrency(priceOption.minimum_rate || priceOption.flat_rate || 0)}
            </Text>
        )
    }

    const renderMultipleDurationsContent = () => {
        return (
            <Flex vertical gap={spacings.S}>
                {tripPrices.map((priceOption, idx) => {
                    const durationLabel = formatTimeDuration(priceOption.trip_duration)

                    return (
                        <Flex key={`duration-${priceOption.id || idx}`} vertical gap={spacings.XXS}>
                            {renderPricingDisplay(priceOption, durationLabel)}

                            {idx < tripPrices.length - 1 && (
                                <Divider style={{ margin: `${spacings.XS} 0`, borderColor: "var(--color-surface2)" }} />
                            )}
                        </Flex>
                    )
                })}
            </Flex>
        )
    }

    const renderTooltipContent = () => {
        return (
            <Flex vertical gap={spacings.XXS} className={styles.tooltipContent}>
                <Title level={4}>{t("client.payment.tooltip.price_details")}</Title>
                <Divider style={{ borderColor: `var(--color-surface2)` }} />
                {renderMultipleDurationsContent()}
            </Flex>
        )
    }

    return (
        <Tooltip title={renderTooltipContent()} overlayInnerStyle={{ width: 300 }}>
            <InfoCircleOutlined className="ml-xxs" style={{ fontSize: 16, color: "var(--color-primary)" }} />
        </Tooltip>
    )
}

PricingDetailsTooltip.propTypes = {
    tripPrices: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            trip_id: PropTypes.number,
            trip_duration: PropTypes.number,
            pricing_type: PropTypes.string,
            flat_rate: PropTypes.string,
            minimum_rate: PropTypes.string,
            price_per_person: PropTypes.string,
            price_per_child: PropTypes.string,
            price_per_infant: PropTypes.string,
            price_per_passenger: PropTypes.string,
            included_guests: PropTypes.number,
            infants_allowed: PropTypes.bool,
            passengers_allowed: PropTypes.bool,
            children_allowed: PropTypes.bool
        })
    )
}

export default PricingDetailsTooltip
